import { AlarmIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { Fragment } from "react/jsx-runtime";
import { useIntl } from "react-intl";

import { Chip } from "@/components/Chip/Chip";
import { Divider } from "@/components/Divider/Divider";
import { extractAirlineCarriers } from "@/features/flights/utils/extractAirlineCarriers";
import { splitSegmentsToFlights } from "@/features/flights/utils/splitSegmentsToFlights";
import type { FindUserReservationsExternalFlightReservation } from "@/types/codegen";
import { parseDuration } from "@/utils/duration";

import FlightArrow from "./FlightArrow";
import { FlightInfo } from "./flights/FlightInfo";
import { SingleAvailableFlight } from "./SingleAvailableFlight";

type Props = {
  itinerary: FindUserReservationsExternalFlightReservation;
};

export function FlightReservationSegmentsData({ itinerary }: Props) {
  const intl = useIntl();

  const allAirlines = extractAirlineCarriers(itinerary.segments);

  const { departureFlight, returnFlight } = useMemo(
    () => splitSegmentsToFlights(itinerary.segments),
    [itinerary.segments],
  );

  return (
    <SingleAvailableFlight airlines={allAirlines}>
      {[departureFlight, returnFlight].map((segments, index) => (
        <Fragment key={index}>
          {index > 0 && segments.length > 0 ? (
            <div className="my-2">
              <Divider color="neutral-softer" />
            </div>
          ) : null}

          {segments.map((segment) => {
            // does operating carrier differ from marketing carrier
            const operatedBy =
              segment.operatingCarrier &&
              segment.marketingCarrier &&
              segment.operatingCarrier.code !== segment.marketingCarrier.code
                ? segment.operatingCarrier.name || segment.operatingCarrier.code
                : undefined;

            return (
              <Fragment key={segment.flightNumber}>
                {segment.stopTime ? (
                  <div
                    className="mx-auto"
                    style={{ marginBlock: "calc(var(--spacing) * -2)" }}
                  >
                    <Chip
                      icon={<AlarmIcon />}
                      color="primary"
                      size="small"
                      label={intl.formatMessage(
                        {
                          defaultMessage: "{hours}h {minutes}m stop",
                          description: "Layover duration indicator",
                        },
                        parseDuration(segment.stopTime),
                      )}
                    />
                  </div>
                ) : null}

                <FlightInfo
                  origin={segment.origin}
                  destination={segment.destination}
                >
                  <div className="flex flex-col">
                    <FlightArrow
                      overallTime={
                        segment.flightDuration
                          ? intl.formatMessage(
                              {
                                defaultMessage: "{hours}h {minutes}m",
                                description:
                                  "Formatted flight or travel duration (e.g. '2h 30m')",
                              },
                              parseDuration(segment.flightDuration),
                            )
                          : null
                      }
                    />
                    {operatedBy ? (
                      <div className="text-center text-sm text-text-tertiary">{`${intl.formatMessage({ defaultMessage: "Flight operated by", description: "Prefix text before the operating carrier name (e.g. 'Flight operated by Fly Baghdad')" })} ${operatedBy}`}</div>
                    ) : null}
                  </div>
                </FlightInfo>
              </Fragment>
            );
          })}
        </Fragment>
      ))}
    </SingleAvailableFlight>
  );
}
